import { sendRequest, sendRequestWithPayload } from "@/common/http";

export type DraftTone = "casual" | "professional";
export type DraftLength = "brief" | "standard" | "detailed";

export interface DraftMessageClosing {
  closingLine: string;
  signOff: string;
  senderName: string;
}

export interface DraftMessageOutput {
  greeting: string;
  introSummary: string;
  body: string[];
  closing: DraftMessageClosing;
}

interface TokenResult {
  token: string;
}

interface DraftMessageResult {
  output: DraftMessageOutput;
}
function fetchCsrfToken(): Promise<string> {
  return new Promise((resolve, reject) => {
    sendRequest(
      { uri: "/export/token", camelize: true },
      (result: TokenResult) => resolve(result.token),
      reject,
    );
  });
}

export async function generateDraftMessage(params: {
  inputMessage?: string | string[];
  tone?: string;
  length?: string;
  currentOutput?: DraftMessageOutput;
  userPrompt?: string;
}): Promise<DraftMessageOutput> {
  const token = await fetchCsrfToken();

  const inputMessages = params.inputMessage
    ? Array.isArray(params.inputMessage) ? params.inputMessage : [params.inputMessage]
    : [];

  const formData = new FormData();
  formData.append("tone", params.tone ?? "");
  formData.append("length", params.length ?? "");
  inputMessages.forEach((message) => formData.append("input_message[]", message));
  formData.append("draft_summary", params.currentOutput ? formatEmailText(params.currentOutput) : "");
  formData.append("user_prompt", params.userPrompt ?? "");
  formData.append("_csrf_token", token);

  return new Promise((resolve, reject) => {
    sendRequestWithPayload(
      { uri: "/ai-chat/draft-message", body: formData, camelize: true },
      (result: DraftMessageResult) => resolve(result.output),
      reject,
    );
  });
}

export function formatEmailText(
  output: DraftMessageOutput,
  senderNameFallback = "",
): string {
  const lines: string[] = [];

  if (output.greeting) lines.push(output.greeting);

  if (output.introSummary) {
    lines.push("");
    lines.push(output.introSummary);
  }

  output.body.forEach((paragraph) => {
    lines.push("");
    lines.push(paragraph);
  });

  const { closingLine, signOff, senderName } = output.closing;
  lines.push("");
  if (closingLine) lines.push(closingLine);
  if (signOff && signOff !== closingLine) lines.push(signOff);
  lines.push(senderName || senderNameFallback);

  return lines.join("\n");
}
